<template>
  <b-modal
    id="subsection-download-modal"
    title="Download analytics report"
    :visible="visible"
    centered
    @hidden="handleHidden"
  >
    <p class="field-label mt-0">Format</p>
    <div class="format-group mb-4">
      <label
        v-for="option in formatOptions"
        :key="option.value"
        class="format-opt"
        :class="{ selected: format === option.value }"
      >
        <input v-model="format" type="radio" :value="option.value" :disabled="isGenerating" />
        <span class="fmt-name">{{ option.label }}</span>
        <span class="fmt-sub">{{ option.sublabel }}</span>
      </label>
    </div>

    <template v-if="showSectionPicker">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <p class="field-label mb-0">Sections</p>
        <button type="button" class="btn btn-link p-0 link-btn" :disabled="isGenerating" @click="toggleSelectAll">
          {{ allSelected ? 'Clear all' : 'Select all' }}
        </button>
      </div>

      <b-form-checkbox
        v-for="section in availableSections"
        :key="section.key"
        v-model="selectedKeys"
        :value="section.key"
        :disabled="isGenerating"
        class="mb-2"
      >
        {{ section.label }}
      </b-form-checkbox>
    </template>

    <template #modal-footer="{ cancel }">
      <b-button variant="outline-secondary" :disabled="isGenerating" @click="cancel">Cancel</b-button>
      <b-button variant="primary" :disabled="!hasSelection || isGenerating" @click="handleDownload">
        <b-spinner v-if="isGenerating" small class="mr-2" />
        <font-awesome-icon v-else :icon="['fad', 'download']" class="mr-2" />
        {{ buttonLabel }}
      </b-button>
    </template>
  </b-modal>
</template>

<script setup>
import { computed, getCurrentInstance, ref, watch } from 'vue';
import useSubsectionDownload, { NoExportableDataError } from '@/composables/useSubsectionDownload';

const props = defineProps({
  sections: {
    type: Array,
    required: true,
  },
  // Resolves page-level extras (e.g. Summary snapshot KPIs, Compare's industry
  // benchmark panel) applied to the first sheet of a multi-section PDF only.
  getPageExtras: {
    type: Function,
    default: null,
  },
  visible: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['hidden']);

const formatOptions = [
  { value: 'pdf', label: 'PDF', sublabel: 'Formatted report' },
  { value: 'xlsx', label: 'Excel', sublabel: 'One sheet per section' },
  { value: 'csv', label: 'CSV', sublabel: '.zip of files' },
];

const format = ref('pdf');
const selectedKeys = ref([]);
const { isGenerating, downloadSheets } = useSubsectionDownload();
const instance = getCurrentInstance();

const availableSections = computed(() => {
  if (format.value === 'pdf') {
    return props.sections;
  }
  return props.sections.filter(s => typeof s.getExportTable === 'function');
});

const showSectionPicker = computed(() => availableSections.value.length > 1);

watch(availableSections, sections => {
  if (sections.length <= 1) {
    // No picker shown — auto-select the single available section so the
    // Download button is enabled without forcing a click on a checkbox.
    selectedKeys.value = sections.map(s => s.key);
    return;
  }
  const allowed = new Set(sections.map(s => s.key));
  selectedKeys.value = selectedKeys.value.filter(k => allowed.has(k));
});

watch(
  () => props.visible,
  isVisible => {
    if (isVisible && availableSections.value.length <= 1) {
      selectedKeys.value = availableSections.value.map(s => s.key);
    }
  },
  { immediate: true },
);

const hasSelection = computed(() => selectedKeys.value.length > 0);
const allSelected = computed(
  () => availableSections.value.length > 0 && selectedKeys.value.length === availableSections.value.length,
);
const buttonLabel = computed(() => {
  if (isGenerating.value) {
    return 'Generating...';
  }
  if (!hasSelection.value) {
    return 'Download';
  }
  return `Download (${selectedKeys.value.length})`;
});

function toggleSelectAll() {
  if (allSelected.value) {
    selectedKeys.value = [];
  } else {
    selectedKeys.value = availableSections.value.map(s => s.key);
  }
}

function resetState() {
  selectedKeys.value = [];
  format.value = 'pdf';
}

function handleHidden() {
  resetState();
  emit('hidden');
}

async function handleDownload() {
  const selected = availableSections.value.filter(s => selectedKeys.value.includes(s.key));
  const pageExtras = props.getPageExtras ? props.getPageExtras() : null;
  const result = await downloadSheets(selected, { format: format.value, pageExtras });
  if (result?.success) {
    resetState();
    emit('hidden');
    return;
  }
  const message =
    result?.error instanceof NoExportableDataError
      ? 'The selected sections have no exportable data yet. Try again once the charts have loaded.'
      : 'Something went wrong generating the download. Please try again.';
  instance?.proxy?.$appToast?.(message, { title: 'Download failed', variant: 'danger' });
}
</script>

<style lang="scss" scoped>
.field-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--palette-color-default);
}

.format-group {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
}

.format-opt {
  position: relative;
  border: 1px solid var(--palette-color-default-lighten-80);
  border-radius: 0.5rem;
  padding: 0.75rem 0.625rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.15s ease;
  background: var(--palette-color-base-white);
  margin: 0;

  input {
    position: absolute;
    opacity: 0;
    pointer-events: none;
  }

  &:hover {
    border-color: var(--palette-color-brand-primary-lighten-30);
  }

  &.selected {
    border-color: var(--palette-color-brand-primary);
    background: var(--palette-color-brand-primary-lighten-90);

    .fmt-name {
      color: var(--palette-color-brand-primary-darken-10);
    }
  }

  .fmt-name {
    display: block;
    font-size: 0.8125rem;
    font-weight: 600;
  }

  .fmt-sub {
    display: block;
    font-size: 0.6875rem;
    color: var(--palette-color-default-lighten-20);
    margin-top: 0.125rem;
  }
}

.link-btn {
  font-size: 0.8125rem;
  font-weight: 500;
  text-decoration: none;

  &:hover:not(:disabled) {
    text-decoration: underline;
  }
}
</style>
